import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Don't redirect here, let the component handle it
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  magicLink: (email) => api.post('/auth/magic-link', { email }),
  verifyMagicLink: (token) => api.post('/auth/magic-link/verify', { token }),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
};

// Users
export const usersAPI = {
  updateProfile: (data) => api.put('/users/profile', data),
  getInvestorProfile: () => api.get('/users/investor-profile'),
  updateInvestorProfile: (data) => api.put('/users/investor-profile', data),
};

// Opportunities
export const opportunitiesAPI = {
  list: (params) => api.get('/opportunities', { params }),
  get: (slug) => api.get(`/opportunities/${slug}`),
  create: (data) => api.post('/admin/opportunities', data),
  update: (id, data) => api.put(`/admin/opportunities/${id}`, data),
  listAll: (params) => api.get('/admin/opportunities', { params }),
};

// Properties
export const propertiesAPI = {
  list: () => api.get('/properties'),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  submit: (id) => api.post(`/properties/${id}/submit`),
  listAll: (params) => api.get('/admin/properties', { params }),
  updateStatus: (id, status, notes) => api.put(`/admin/properties/${id}/status`, { status, notes }),
};

// Commitments
export const commitmentsAPI = {
  list: () => api.get('/commitments'),
  create: (data) => api.post('/commitments', data),
  listAll: (params) => api.get('/admin/commitments', { params }),
  updateStatus: (id, status, notes) => api.put(`/admin/commitments/${id}/status`, { status, notes }),
};

// Documents
export const documentsAPI = {
  list: (params) => api.get('/documents', { params }),
  upload: (file, category, description, relatedType, relatedId) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ category });
    if (description) params.append('description', description);
    if (relatedType) params.append('related_entity_type', relatedType);
    if (relatedId) params.append('related_entity_id', relatedId);
    return api.post(`/documents/upload?${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
  listAll: (params) => api.get('/admin/documents', { params }),
};

// Contact
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  listLeads: (params) => api.get('/admin/leads', { params }),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUserVerification: (userId, status, notes) => 
    api.put(`/admin/users/${userId}/verification`, { status, notes }),
  updateUserRole: (userId, role) => 
    api.put(`/admin/users/${userId}/role`, { role }),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
};

// Dashboard
export const dashboardAPI = {
  investor: () => api.get('/dashboard/investor'),
  propertyContributor: () => api.get('/dashboard/property-contributor'),
};

export default api;
